import { createEffect, createEvent, createStore, sample } from 'effector';
import { ISearchByParams } from '../components/mixing-by-params-modal/hooks';
import { AnalyticsMixingService } from '../services/analytics-mixing.service';
import { DiContainer } from '@/modules/di/di-container';
import { Value } from '@/modules/warehouses/ts';

const analyticsService = DiContainer.get(AnalyticsMixingService);

export const setParamsModalOpen = createEvent<{ payload: boolean }>();
export const setActivesModalOpen = createEvent<{ payload: boolean }>();
export const $paramsModalOpen = createStore(false).on(
  setParamsModalOpen,
  (_state, { payload }: { payload: boolean }) => {
    return payload;
  },
);
export const $activesModalOpen = createStore(false).on(
  setActivesModalOpen,
  (_state, { payload }: { payload: boolean }) => {
    return payload;
  },
);

export const $analyticalMixingSearchStore = createStore<ISearchByParams>({
  stores: [],
  permissibleVariation: 0,
  gluten: 0,
  idk: 0,
  protein: 0,
  humidity: 0,
  natura: 0,
  fallingNumber: 0,
  temperature: 0,
});

export const updateAnalyticalMixingSearchStore = createEvent<any>();

$analyticalMixingSearchStore.on(
  updateAnalyticalMixingSearchStore,
  (_, payload: any) => payload,
);

// ==========

const getBestMarketPrices = createEvent<any>();
export const setInitialStateBestMarketPrices = createEvent();

export const $bestMarketPrices = createStore<BestPriceWarehouseItem[] | null>(null)
  .on(getBestMarketPrices, (_, newData) => {
    return newData;
  })
  .on(setInitialStateBestMarketPrices, () => null);

export const getBestMarketPricesFx = createEffect(async (trade?: boolean) => {
  try {
    const data = await analyticsService.getBestMarketPrices(trade, true);
    return data;
  } catch (err: any) {
    const error = await err.json();
    return { error };
  }
});

export const $bestMarketPricesLoading = createStore<{
  loading: boolean;
  error: boolean;
}>({ loading: false, error: false })
  .on(getBestMarketPricesFx.pending, () => {
    return { loading: true, error: false };
  })
  .on(getBestMarketPricesFx.doneData, () => {
    return { loading: false, error: false };
  })
  .on(getBestMarketPricesFx.failData, () => {
    return { loading: false, error: true };
  });

sample({
  clock: getBestMarketPricesFx.doneData,
  target: getBestMarketPrices,
});

export const addExpandedStore = createEvent<{ id: number; expanded: boolean }>();
export const $expandedStoresArr = createStore<Array<number>>([]).on(
  addExpandedStore,
  (state, payload) => {
    if (payload.expanded) {
      return [...state, payload.id];
    } else if (payload.expanded === false) {
      const modArr = state.filter((item) => item !== payload.id);
      return modArr;
    } else return state;
  },
);

// =====

const getBestMarketPricesTab = createEvent<any>();
export const setInitialStateBestMarketPricesTab = createEvent();

export const $bestMarketPricesTab = createStore<BestPriceWarehouseItem[] | null>(null)
  .on(getBestMarketPricesTab, (_, newData) => {
    return newData;
  })
  .on(setInitialStateBestMarketPricesTab, () => null);

export const getBestMarketPricesTabFx = createEffect(
  async (args: { trade?: boolean; moistureChangesDensity?: boolean }) => {
    try {
      setBestMarketPricesTabLoading({ loading: true, error: false });
      const data = await analyticsService.getBestMarketPrices(
        args.trade,
        args.moistureChangesDensity,
      );
      setBestMarketPricesTabLoading({ loading: false, error: false });
      return data;
    } catch (err: any) {
      const error = await err.json();
      setBestMarketPricesTabLoading({ loading: false, error });
      return { error };
    }
  },
);

const setBestMarketPricesTabLoading = createEvent<{
  loading: boolean;
  error: boolean | Error | string;
}>();

export const $bestMarketPricesTabLoading = createStore<{
  loading: boolean;
  error: boolean | Error | string;
}>({ loading: false, error: false })
  .on(setBestMarketPricesTabLoading, (_s, p) => p);


sample({
  clock: getBestMarketPricesTabFx.doneData,
  target: getBestMarketPricesTab,
});

export interface BestPriceWarehouseItem {
  bags: IBestPriceBagItem[];
  delta: number;
  expired_at: Date;
  freight_price: number;
  id: number;
  logistic_price: number;
  unmix_freight: number;
  mix_price: number;
  mix_profit: number;
  name: string;
  netto: number;
  price: number;
  weight: number;
  expanded?: boolean;
  weight_delta: number;
}

export interface BestPriceBagItem {
  buyers: BestPriceBuyerItem[];
  class: string;
  id: string;
  status: string;
  weight: number;
  values: Value[];
}

export interface IBestPriceBagItem {
  all_price: number;
  agriculture: string;
  agriculture_id: number;
  buyers: BestPriceBuyerItem[];
  class: string;
  destination_id: number;
  freight_cost_after_moisture_mod: number;
  freight_cost_before_moisture_mod: number;
  freight_cost_tkm: number;
  id: string;
  is_wheat: boolean;
  mixes: number[];
  new_nature: number;
  price_id: number;
  status: string;
  trade_id?: number;
  values: AgricultureMetrics;
  weight: number;
  weight_gain_tons: number;
  initial_weight_gain_tons: number;
  offer_humidity: number;
  sort: string;
  type: string;
}

export interface AgricultureMetrics {
  gluten: string;
  humidity: string;
  idk: string;
  cp: string;
  protein: string;
  nature: string;
  temperature: string;
  type: string;
  class: string;
}

export interface BestPriceBuyerItem {
  buyer: string;
  buyer_id: number;
  class: string;
  company_id: number;
  distance?: number;
  freight_price_per_ton: number;
  freight_price: number;
  netto: number;
  price_all: number;
  price_per_ton: number;
  type: string;
}
